/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useHotDeskingData.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for loading hot desking device data and options.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchHotDeskingDeviceById } from '../api/hotDeskingApi';
import { fetchDeviceSipProfiles, DeviceSipProfile } from '../../../services/deviceSipProfiles';
import { buildApiUrl, getApiHeaders } from '../../../config/api';
import { getSettingValue } from '../utils/settingsHelpers';
import { createDefaultFormData } from '../utils/formDefaults';
import { generateDefaultSecret } from '../utils/passwordGenerators';
import type { HotDeskFormState } from '../types';
import type { DispatchLocationOption } from '../../../components/ui/SelectDispatchLocation';

const DEFAULT_MAX_REGISTRATIONS = '5';
const DEFAULT_CODECS = 'PCMU,PCMA';
const DEFAULT_DTMF_MODE = 'rfc4733';
const DEFAULT_LANGUAGE_CODE = 'en-us-emma';

interface UseHotDeskingDataOptions {
  id?: string;
  duplicateId?: string | null;
  isEditMode: boolean;
  selectedDomainId?: number;
}

export function useHotDeskingData({
  id,
  duplicateId,
  isEditMode,
  selectedDomainId,
}: UseHotDeskingDataOptions) {
  const location = useLocation();
  const [formData, setFormData] = useState<HotDeskFormState>(createDefaultFormData);
  const [sipProfiles, setSipProfiles] = useState<DeviceSipProfile[]>([]);
  const [dispatchLocations, setDispatchLocations] = useState<DispatchLocationOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const lastLoadedIdRef = useRef<string | null>(null);
  const lastLocationKeyRef = useRef<string>('');

  // Load options (SIP Profiles + Dispatch Locations)
  useEffect(() => {
    const loadOptions = async () => {
      setIsLoadingOptions(true);
      try {
        const [profilesResult, dispatchResult] = await Promise.allSettled([
          selectedDomainId ? fetchDeviceSipProfiles(selectedDomainId) : Promise.resolve([]),
          fetch(buildApiUrl('/api/telephony/dispatch-locations?limit=200&sortBy=locationName&sortOrder=asc'), { headers: getApiHeaders() })
            .then(r => r.json())
            .then(res => Array.isArray(res?.data) ? res.data : [])
            .catch(() => []),
        ]);

        if (profilesResult.status === 'fulfilled') {
          setSipProfiles(profilesResult.value);
        }

        if (dispatchResult.status === 'fulfilled') {
          const raw = dispatchResult.value as Array<{ id: string | number; locationName?: string; location_name?: string; formattedAddress?: string; formatted_address?: string; elin?: string }>;
          setDispatchLocations(raw.map(d => ({
            id: Number(d.id),
            locationName: d.locationName ?? d.location_name ?? '',
            formattedAddress: d.formattedAddress ?? d.formatted_address ?? '',
            elin: d.elin ?? '',
          })));
        }
      } catch (error) {
        console.error('Error loading hot desking options:', error);
      } finally {
        setIsLoadingOptions(false);
      }
    };

    loadOptions();
  }, [selectedDomainId]);

  // Load device data if editing or duplicating
  const loadDevice = useCallback(async (deviceId: string, duplicate: boolean = false) => {
    setIsLoading(true);
    try {
      const device = await fetchHotDeskingDeviceById(deviceId);

      const loadedData: HotDeskFormState = {
        username: device.username || '',
        password: getSettingValue(device.settings, 'password') || 'r2a2025',
        description: device.description || '',
        sipProfileId: device.sip_profile_id || null,
        dispatchLocationId: (device.settings as Record<string, unknown> & { dispatch_location_id?: number | null })?.dispatch_location_id ?? null,
        maxRegistrations: getSettingValue(device.settings, 'max-registrations') || DEFAULT_MAX_REGISTRATIONS,
        outboundCodecs: getSettingValue(device.settings, 'outbound-codec-prefs') || DEFAULT_CODECS,
        inboundCodecs: getSettingValue(device.settings, 'inbound-codec-prefs') || DEFAULT_CODECS,
        dtmfMode: getSettingValue(device.settings, 'dtmf-type') || DEFAULT_DTMF_MODE,
        language: getSettingValue(device.settings, 'default_language') || DEFAULT_LANGUAGE_CODE,
        enabled: device.enabled ?? true,
      };

      // If duplicating, clear username and regenerate password
      if (duplicate) {
        loadedData.username = '';
        loadedData.password = generateDefaultSecret();
        if (loadedData.description) {
          loadedData.description = `${loadedData.description} Copy`.trim();
        }
        loadedData.dispatchLocationId = null;
      }

      setFormData(loadedData);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load device when id or duplicateId changes
  useEffect(() => {
    const currentId = id || 'new';
    const savedDataKey = 'hotDesking:saveAndNewData';
    const locationKey = location.key || location.pathname + location.search;

    if (isEditMode && id && id !== 'new') {
      loadDevice(id);
      lastLoadedIdRef.current = id;
      lastLocationKeyRef.current = locationKey;
    } else if (!isEditMode && duplicateId) {
      loadDevice(duplicateId, true);
      lastLoadedIdRef.current = duplicateId;
      lastLocationKeyRef.current = locationKey;
    } else if (!isEditMode && !duplicateId) {
      const resetTimestamp = sessionStorage.getItem(savedDataKey + ':reset');
      const lastResetTimestamp = lastLoadedIdRef.current === 'new' ?
        sessionStorage.getItem(savedDataKey + ':lastReset') : null;

      const shouldReset = lastLoadedIdRef.current !== 'new' ||
        lastLocationKeyRef.current !== locationKey ||
        (lastLoadedIdRef.current === 'new' && currentId === 'new' &&
          resetTimestamp && resetTimestamp !== lastResetTimestamp);

      if (shouldReset) {
        let preservedFormData: Partial<HotDeskFormState> = {};

        try {
          const stored = sessionStorage.getItem(savedDataKey);
          if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed) {
              preservedFormData = {
                sipProfileId: parsed.sipProfileId,
                maxRegistrations: parsed.maxRegistrations,
                outboundCodecs: parsed.outboundCodecs,
                inboundCodecs: parsed.inboundCodecs,
                dtmfMode: parsed.dtmfMode,
                language: parsed.language,
              };
            }
          }
          sessionStorage.removeItem(savedDataKey);
          if (resetTimestamp) {
            sessionStorage.setItem(savedDataKey + ':lastReset', resetTimestamp);
            sessionStorage.removeItem(savedDataKey + ':reset');
          }
        } catch (error) {
          // Ignore errors when clearing
        }

        setFormData({ ...createDefaultFormData(), ...preservedFormData });
        lastLoadedIdRef.current = 'new';
        lastLocationKeyRef.current = locationKey;
      }
    }
  }, [id, isEditMode, duplicateId, loadDevice, location.key, location.pathname, location.search]);

  return {
    formData,
    setFormData,
    sipProfiles,
    dispatchLocations,
    isLoading,
    isLoadingOptions,
  };
}


